import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/navbar/Navbar";
import Card from "../../components/card/Card";
import LoadingAnimation from "../../components/loading-animation/LoadingAnimation";
import "./TestApis.css";
import { isStaffUser } from "../../utils/session";

/* ===================== Helpers d'erreur / retry ===================== */
class HttpError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.payload = payload;
  }
}

const ERROR_TEXTS = {
  CODE_ALREADY_USED: "Ce code est déjà utilisé.",
  INVALID_CODE: "Code invalide, merci de vérifier le code saisi.",
  CODE_NOT_FOUND: "Aucun dossier trouvé pour ce code.",
  CODE_INTROUVABLE: "Aucun dossier trouvé pour ce code.",
  UNAUTHORIZED: "Session expirée. Merci de vous reconnecter.",
  FORBIDDEN: "Accès refusé.",
  BAD_GATEWAY: "Service momentanément indisponible. Réessayez.",
  TIMEOUT: "Délai dépassé. Vérifiez votre connexion.",
  SERVER_ERROR: "Code Incorrect.",
  BAD_PAYLOAD: "Réponse invalide du service.",
  UNEXPECTED_ERROR: "Une erreur inattendue est survenue.",
  NETWORK_ERROR: "Erreur réseau. Merci de réessayer.",
  MISSING_USER_ID: "Session expirée. Merci de vous reconnecter.",
  MISSING_ORGANISATION_ID: "Choisissez l'organisation à laquelle rattacher le dossier.",
  INVALID_ORGANISATION_ID: "Cette organisation n'est pas une organisation cliente.",
  INACTIVE_ORGANISATION: "Cette organisation est désactivée.",
  FORBIDDEN_ORGANISATION: "Vous ne pouvez rattacher un dossier qu'à votre organisation.",
};

const fetchJson = async (url, options) => {
  const res = await fetch(url, options);
  let data = null;
  try {
    data = await res.json();
  } catch {
    // ignorer
  }
  if (!res.ok) {
    const codeFromApi = data?.error_code;
    const msgFromApi = data?.message;
    throw new HttpError(msgFromApi || `HTTP ${res.status}`, res.status, {
      error_code: codeFromApi,
      raw: data,
    });
  }
  return data;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
/* ==================================================================== */
const TestApis = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  // Organisation du dossier : VeoSmart la choisit (obligatoire) ; un user
  // client n'a pas de choix, le backend prend la sienne.
  const mustChooseOrganisation = isStaffUser();
  const [organisations, setOrganisations] = useState([]);
  const [organisationId, setOrganisationId] = useState("");
  const [organisationsError, setOrganisationsError] = useState(false);

  useEffect(() => {
    if (!mustChooseOrganisation) return;
    const token = localStorage.getItem("access_token");
    fetch("/api/organisations/clients", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((orgs) => {
        setOrganisations(Array.isArray(orgs) ? orgs : []);
        setOrganisationsError(false);
      })
      .catch(() => {
        setOrganisations([]);
        setOrganisationsError(true);
      });
  }, [mustChooseOrganisation]);
  const navigate = useNavigate();

  const showErrorPopup = (message) => {
    setErrorMessage(message);
    setShowPopup(true);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");
    return {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    };
  };

  const normalizeCode = (input) => {
    if (typeof input === "string") return input.trim();
    if (input && typeof input === "object") {
      // Try to extract code from various possible properties
      const codeValue = input.code || input.weproov_code || input.value || "";
      return codeValue.toString().trim();
    }
    return "";
  };


  const navigateToResults = (raw) => {
    console.log("navigateToResults raw:", raw); // Debug log to see what's being passed

    const code = normalizeCode(raw);
    console.log("navigateToResults normalized code:", code); // Debug log

    if (!code) {
      showErrorPopup("Veuillez entrer un code valide.");
      return;
    }

    // Ensure code is a string and encode it
    const encodedCode = encodeURIComponent(String(code));
    console.log("navigateToResults encoded code:", encodedCode); // Debug log

    navigate(`/result-page/${encodedCode}`);
  };


  // ✅ Appeler ApiGlobale (sans retry pour éviter les doublons)
  const fetchApiGlobale = async (code) => {
    const data = await fetchJson(
      `/api/ApiGlobale/${code}` +
        (mustChooseOrganisation ? `?organisation_id=${encodeURIComponent(organisationId)}` : ""),
      { method: "GET", headers: getAuthHeaders() }
    );
    if (!data.personal_info) {
      throw new HttpError("Données incomplètes (API Globale)", 502, {
        error_code: "BAD_PAYLOAD",
      });
    }
    return data;
  };

  // ✅ GLOBALE seul, SANS retry (CENTRE n'est plus utilisé)
  const tryApi = async (code) => {
    console.log("tryApi - calling ApiGlobale with code:", code);
    await fetchApiGlobale(code);
    console.log("tryApi - ApiGlobale succeeded, navigating with code:", code);
    navigateToResults(code);
  };


  const mapAndShowError = (error) => {
    if (error instanceof HttpError) {
      const apiCode = error.payload?.error_code;
      const byCode = apiCode && ERROR_TEXTS[apiCode];
      if (byCode) return showErrorPopup(byCode);

      const byStatus =
        {
          400: ERROR_TEXTS.INVALID_CODE,
          401: ERROR_TEXTS.UNAUTHORIZED,
          403: ERROR_TEXTS.FORBIDDEN,
          404: ERROR_TEXTS.CODE_NOT_FOUND,
          409: ERROR_TEXTS.CODE_ALREADY_USED,
          500: ERROR_TEXTS.SERVER_ERROR,
          502: ERROR_TEXTS.BAD_GATEWAY,
          504: ERROR_TEXTS.TIMEOUT,
        }[error.status] || ERROR_TEXTS.UNEXPECTED_ERROR;

      return showErrorPopup(byStatus);
    }
    showErrorPopup(ERROR_TEXTS.NETWORK_ERROR);
  };

  const handleNewDossier = async (raw) => {
    const code = normalizeCode(raw);
    if (!code) return showErrorPopup("Veuillez entrer un code valide.");
    if (mustChooseOrganisation && !organisationId) {
      return showErrorPopup(ERROR_TEXTS.MISSING_ORGANISATION_ID);
    }
    const token = localStorage.getItem("access_token");
    if (!token) return showErrorPopup(ERROR_TEXTS.UNAUTHORIZED);

    setIsLoading(true);
    try {
      await tryApi(code);        // ← tryApi prend maintenant une string sûre
    } catch (error) {
      mapAndShowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuperviseDossier = async (raw) => {
    const weproovCode = normalizeCode(raw);
    console.log("handleSuperviseDossier weproovCode:", weproovCode); // Debug log

    if (!weproovCode) return showErrorPopup("Veuillez entrer un code WeProov valide.");
    const token = localStorage.getItem("access_token");
    if (!token) return showErrorPopup(ERROR_TEXTS.UNAUTHORIZED);

    setIsLoading(true);
    try {
      await fetchJson(`/api/WeproovCode/${weproovCode}`, {
        headers: getAuthHeaders(),
      });

      // Ensure weproovCode is a string and encode it
      const encodedCode = encodeURIComponent(String(weproovCode));
      console.log("handleSuperviseDossier encoded code:", encodedCode); // Debug log

      navigate(`/result-page/${encodedCode}`);
    } catch (error) {
      mapAndShowError(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (!showPopup) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setShowPopup(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showPopup]);

  return (
    <div className="test-apis-page">
      <Navbar username={localStorage.getItem("username") || "Guest"} />
      <div className="cards-container">
        <Card
          onNewDossier={handleNewDossier}
          onSuperviseDossier={handleSuperviseDossier}
          showOrganisation={mustChooseOrganisation}
          organisations={organisations}
          organisationId={organisationId}
          onOrganisationChange={setOrganisationId}
          organisationsError={organisationsError}
        />
      </div>

      {isLoading && <LoadingAnimation />}

      {showPopup && (
        <div className="error-popup" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowPopup(false); }}>
          <div className="error-content" role="alertdialog" aria-modal="true">
            <p>{errorMessage}</p>
            <button onClick={() => setShowPopup(false)} autoFocus>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestApis;
